import { useApolloClient } from '@apollo/client';
import { Stack, Typography } from '@mui/material';
import { useCallback } from 'react';

import { Button } from '@/components/form/baseInputs/Button/Button';
import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { Copy07Icon } from '@/components/icons/Copy07Icon';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { Card } from '@/components/layout/Card/Card';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { TypographyClamped } from '@/components/typography/TypographyClamped';
import { useIsElementHovered } from '@/hooks/useIsElementHovered';
import { useModalState } from '@/hooks/useModalState';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { AIOnboardingSuggestionDrawer } from '@/modules/aiSuggestions/components/AIOnboardingSuggestionDrawer';
import { LocateSuggestionButton } from '@/modules/aiSuggestions/components/LocateSuggestionButton';
import { RegenerateSubtrustSuggestionsV2MenuItem } from '@/modules/aiSuggestions/components/RegenerateSubtrustSuggestionsV2MenuItem';
import { DeleteTestamentaryEntityMenuItem } from '@/modules/entities/components/DeleteTestamentaryEntityMenuItem';
import { EditTestamentaryEntitySection } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreen.types';
import { useDuplicateTestamentaryEntityMutation } from '@/modules/entities/testamentaryEntities/graphql/CreateTestamentaryEntity.generated';
import { testamentaryEntityKindToDisplayName } from '@/modules/entities/testamentaryEntities/testamentaryEntities.utils';
import { useEstateOnboardingUnacknowledgedSuggestionsContext } from '@/modules/estate/estateAIOnboarding/context/estateOnboardingUnacknowledgedSuggestions.context';
import { EstateOnboardingTestamentaryEntitySuggestionsButton } from '@/modules/estate/estateAIOnboarding/EstateOnboardingTestamentaryEntitySuggestionsButton';
import {
  EstatePage_TestamentaryEntityFragment,
  EstatePage_UnacknowledgedAiSuggestionFragment,
  EstatePageDataDocument,
} from '@/modules/estate/graphql/EstatePageData.generated';
import { useEstateSuggestionGenerationState } from '@/modules/estate/hooks/useEstateSuggestionGenerationState';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { WorkflowTrackerKind } from '@/types/schema';
import { getIdSafeString } from '@/utils/formatting/strings';

export interface EstateSubtrustCardProps {
  subtrust: EstatePage_TestamentaryEntityFragment;
  entitySuggestionsV2?: EstatePage_UnacknowledgedAiSuggestionFragment[];
  canEdit: boolean;
  onAISuggestionsClick: (
    subtrust: EstatePage_TestamentaryEntityFragment,
    section: EditTestamentaryEntitySection
  ) => void;
  onViewSubtrustDetails: (
    subtrust: EstatePage_TestamentaryEntityFragment
  ) => void;
}

export function EstateSubtrustCard({
  subtrust,
  entitySuggestionsV2,
  canEdit,
  onAISuggestionsClick,
  onViewSubtrustDetails,
}: EstateSubtrustCardProps) {
  const apolloClient = useApolloClient();
  const [isHovered, hoverProps] = useIsElementHovered();
  const { householdId } = useHouseholdDetailsContext();
  const { refetch: refetchSuggestions } =
    useEstateOnboardingUnacknowledgedSuggestionsContext();
  const trackUserEvent = useTrackUserEvent();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const isGeneratingSubtrustDetailsSuggestions =
    useEstateSuggestionGenerationState({
      entityId: subtrust.id,
      workflowTrackerKind:
        WorkflowTrackerKind.AiSuggestionsV2SubtrustDetailsGeneration,
      onCompleted: refetchSuggestions,
    });
  const isGeneratingDPSuggestions = useEstateSuggestionGenerationState({
    entityId: subtrust.id,
    workflowTrackerKind: WorkflowTrackerKind.AiSuggestionsV2DpGeneration,
    onCompleted: refetchSuggestions,
  });
  const isGeneratingSuggestions =
    isGeneratingSubtrustDetailsSuggestions || isGeneratingDPSuggestions;

  const [duplicateEntity, { loading: isDuplicating }] =
    useDuplicateTestamentaryEntityMutation({
      variables: { id: subtrust.id },
      refetchQueries: [EstatePageDataDocument],
      awaitRefetchQueries: true,
      onError: (error) => {
        reportError('failed to duplicate testamentary entity', error);
        showFeedback(
          'Failed to duplicate this entity. Please refresh the page and try again.'
        );
      },
      onCompleted: () => {
        trackUserEvent('testamentary_entity duplicated');
        showFeedback('Sub-trust duplicated successfully', {
          variant: 'success',
        });
      },
    });

  const allowEdit = isHovered && canEdit;

  // Modal state for Suggestion Document Drawer
  const [suggestionDrawerState, suggestionDrawerModal] = useModalState<{
    suggestionId: string;
  }>();

  const handleLocateSuggestion = useCallback(() => {
    const suggestion = subtrust.onboardingTestamentaryEntitySuggestion?.[0];
    if (suggestion) {
      suggestionDrawerModal.openModal({ suggestionId: suggestion.id });
    }
  }, [subtrust.onboardingTestamentaryEntitySuggestion, suggestionDrawerModal]);

  const subtitle = testamentaryEntityKindToDisplayName(subtrust.kind);

  const handleSectionClick = useCallback(
    (section: EditTestamentaryEntitySection) => {
      onAISuggestionsClick(subtrust, section);
    },
    [onAISuggestionsClick, subtrust]
  );

  return (
    <>
      <Card
        variant="outlined"
        data-testid={`estate-subtrust-card-${getIdSafeString(subtrust.displayName)}`}
        {...hoverProps}
      >
        <Stack spacing={1} px={2} py={1.5} direction="row" alignItems="center">
          <Stack spacing={0.5} flexGrow={1}>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography
                variant="title-xs"
                onClick={
                  allowEdit
                    ? (e) => {
                        e.stopPropagation();
                        onViewSubtrustDetails(subtrust);
                      }
                    : undefined
                }
                sx={{
                  textDecoration: allowEdit ? 'underline' : 'none',
                  cursor: allowEdit ? 'pointer' : 'default',
                  transition: 'text-decoration 0.2s ease, cursor 0.2s ease',
                }}
              >
                {subtrust.displayName}
              </Typography>
              {isHovered &&
                subtrust.onboardingTestamentaryEntitySuggestion?.[0] && (
                  <LocateSuggestionButton
                    suggestionID={
                      subtrust.onboardingTestamentaryEntitySuggestion[0].id
                    }
                    onLocateSuggestionClick={handleLocateSuggestion}
                    dataTestId={`subtrust-locate-suggestion-${subtrust.id}`}
                    iconProps={{ size: 12 }}
                    sx={{
                      width: '16px',
                      minWidth: '16px',
                      height: '16px',
                      p: '2px',
                    }}
                  />
                )}
            </Stack>
            <TypographyClamped
              variant="body-xs"
              lines={1}
              tooltipProps={{ title: subtitle }}
            >
              {subtitle}
            </TypographyClamped>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <EstateOnboardingTestamentaryEntitySuggestionsButton
              testamentaryEntityId={subtrust.id}
              isGenerating={isGeneratingSuggestions}
              entitySuggestionsV2={entitySuggestionsV2}
              onEditTestamentaryEntitySectionSuggestionClick={
                handleSectionClick
              }
            />
            {canEdit && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  data-testid="edit-subtrust-button"
                  variant="secondary"
                  size="xs"
                  startIcon={Edit02Icon}
                  iconProps={{
                    size: 16,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewSubtrustDetails(subtrust);
                  }}
                >
                  Edit
                </Button>
                {householdId && (
                  <ContextMenuButton size="xs" variant="transparent">
                    <MenuItem
                      label="Duplicate"
                      icon={<Copy07Icon size={16} />}
                      loading={isDuplicating}
                      onClick={() => duplicateEntity()}
                    />
                    <RegenerateSubtrustSuggestionsV2MenuItem
                      testamentaryEntityId={subtrust.id}
                      onCompleted={refetchSuggestions}
                    />
                    <DeleteTestamentaryEntityMenuItem
                      testamentaryEntityId={subtrust.id}
                      onDelete={() => {
                        void apolloClient.refetchQueries({
                          include: [EstatePageDataDocument],
                        });
                      }}
                      modalProps={{
                        heading: 'Delete sub-trust?',
                        children: 'This action cannot be undone.',
                      }}
                    />
                  </ContextMenuButton>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Card>
      {suggestionDrawerState.isModalOpen && suggestionDrawerState.data && (
        <AIOnboardingSuggestionDrawer
          isOpen={suggestionDrawerState.isModalOpen}
          onClose={() => suggestionDrawerModal.closeModal()}
          suggestionId={suggestionDrawerState.data.suggestionId}
        />
      )}
    </>
  );
}
